import { useTranslation } from 'react-i18next';
import CatalogListHeader from '../../../components/CatalogListHeader/CatalogListHeader';
import { ActionIcon, Grid, Select, Tooltip } from '@mantine/core';
import { useLocation } from 'react-router-dom';
import CatalogContainer from '../../../components/CatalogContainer/CatalogContainer';
import moment from 'moment';
import useDownloadExcel from '../../../hooks/useDownloadExcel/useDownloadExcel';
import { DatePickerInput, DateValue } from '@mantine/dates';
import { useEffect, useMemo, useState } from 'react';
import { useGetAttendanceReport } from '../../../hooks/reports/queries';
import { exportAttendanceReport } from '../../../services/reports.service';
import { IconFileSpreadsheet } from '@tabler/icons-react';
import TableView from './TableView';
import { MRT_PaginationState } from 'mantine-react-table';
import { getRangeDate } from '../datePickerHelper';
import { useGetStoreStaff } from '../../../hooks/staff/queries';
import { useSelector } from 'react-redux';
import { AttendanceLocationMetadata } from '../../../services/attendance.service';

export interface AttendanceReportTableInfoData {
  employee: {
    _id: string;
    name: string;
    email: string;
  };
  clockInTime: Date;
  clockOutTime: Date | null;
  shiftDuration: {
    hours: number;
    minutes: number;
    totalMinutes: number;
  } | null;
  clockInStore: string;
  clockOutStore: string | null;
  notes: string;
  clockInIpAddress: string;
  clockOutIpAddress: string;
  clockInLocation?: AttendanceLocationMetadata | null;
  clockOutLocation?: AttendanceLocationMetadata | null;
  clockInLocationSummary?: string;
  clockOutLocationSummary?: string;
  autoClosed: boolean;
  _id: string;
}

const AttendanceReport = () => {
  const { t } = useTranslation(['ReportsPage', 'FieldName', 'CommonButtons', 'Notifications']);
  const location = useLocation();
  const {
    company: { _id: companyId }
  } = useSelector((state: any) => state.user);
  const { downloadExcelV2, loadingExport } = useDownloadExcel();
  const [dateRangePicker, setDateRange] = useState<[DateValue, DateValue]>([
    moment().startOf('month').toDate(),
    moment().endOf('month').toDate()
  ]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);

  const { fromDate, toDate } = getRangeDate(dateRangePicker);
  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId = searchParams.get('storeId') || '';

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 100 //tmp fix, since for eg. if page size is 15 and you go to page 2, there's the possibility that clock out is not inide that range/page so it will be considered as still aticve/clock in without clock out
  });

  // 👥 Obtener lista de empleados para el selector
  const {
    isLoading: isGetStoreStaffLoading,
    data: staffs,
    refetch: refetchList
  } = useGetStoreStaff(!!searchParamStoreId, searchParamStoreId);
  const employeeOptions = useMemo(() => {
    return (staffs || []).map((user: any) => ({
      value: user._id,
      label: user.name || user.email
    }));
  }, [staffs]);

  // 📊 Obtener datos del reporte
  const {
    data,
    isLoading: isGetLoadingAttendance,
    isError
  } = useGetAttendanceReport(searchParamStoreId, {
    fromDate,
    toDate,
    employeeId: selectedEmployeeId,
    pageNum: pagination.pageIndex + 1, // Convert from 0-based to 1-based
    limit: pagination.pageSize
  });
  // Datos temporales para desarrollo
  const tableInfoData: AttendanceReportTableInfoData[] = useMemo(() => {
    return (data?.tableInfo || []).map((item: any) => ({
      employee: {
        _id: item.employee._id,
        name: item.employee.name,
        email: item.employee.email
      },
      clockInTime: item.clockInTime,
      clockOutTime: item.clockOutTime,
      shiftDuration: item.shiftDuration,
      clockInStore: item.clockInStore,
      clockOutStore: item.clockOutStore,
      notes: item.notes,
      clockInIpAddress: item.clockInIpAddress,
      clockOutIpAddress: item.clockOutIpAddress,
      clockInLocation: item.clockInLocation,
      clockOutLocation: item.clockOutLocation,
      clockInLocationSummary: item.clockInLocationSummary,
      clockOutLocationSummary: item.clockOutLocationSummary,
      autoClosed: item.autoClosed,
      _id: item._id
    }));
  }, [data?.tableInfo]);

  useEffect(() => {
    setSelectedEmployeeId(null);
  }, [searchParamStoreId]);

  const totalRowsCount = useMemo(() => {
    return data?.pagination?.totalRecords || 0;
  }, [data?.pagination?.totalRecords]);
  const pages = useMemo(() => data?.pagination?.totalPages || -1, [data?.pagination?.totalPages]);

  return (
    <CatalogContainer>
      <CatalogListHeader title={t('ReportsPage:attendanceReport.title')} showCreateButton={false} />
      <Grid>
        <Grid.Col span={{ xs: 12, md: 'auto' }}>
          <DatePickerInput
            clearable={false}
            type="range"
            label={t('FieldName:date')}
            placeholder={t('FieldName:date')}
            value={dateRangePicker}
            onChange={setDateRange}
            allowSingleDateInRange
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 'auto' }}>
          <Select
            label={t('FieldName:employee')}
            placeholder="Todos los empleados"
            value={selectedEmployeeId}
            onChange={setSelectedEmployeeId}
            data={[{ value: '', label: 'Todos los empleados' }, ...employeeOptions]}
            clearable
            searchable
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 'content' }} style={{ alignSelf: 'flex-end' }}>
          <Tooltip label={t('CommonButtons:exportList')}>
            <ActionIcon
              disabled={loadingExport}
              onClick={() => {
                downloadExcelV2(async () => {
                  try {
                    return await exportAttendanceReport(searchParamStoreId, {
                      fromDate,
                      toDate,
                      employeeId: selectedEmployeeId
                    });
                  } catch (e) {
                    throw e;
                  }
                }, `Reporte_Asistencia_${searchParamStoreId}`);
              }}
              variant="filled"
              color="#1D6F42"
              aria-label="Descargar_Reporte_Asistencia">
              <IconFileSpreadsheet style={{ width: '70%', height: '70%' }} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
        </Grid.Col>
        <Grid.Col span={12}>
          <TableView
            tableInfoData={tableInfoData}
            totalRowsCount={totalRowsCount}
            pages={pages}
            isError={isError}
            isLoading={isGetLoadingAttendance}
            pagination={pagination}
            setPagination={setPagination}
          />
        </Grid.Col>
      </Grid>
    </CatalogContainer>
  );
};

export default AttendanceReport;
